import React, { useEffect, useState, useRef } from "react";
import {
  GetPurchaseVoucherList,
  DeletePurchaseVoucher,
  ApprovePurchaseVoucher,
  ExportPurchaseVouchersToExcel,
} from "../../../Services/Services";
import config from "../../../config";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Modal,
  Box,
  TextField,
  MenuItem,
  Popover,
  Typography,
  Checkbox,
  Autocomplete,
  Grid,
  Chip,
  Menu,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  Tooltip,
  alpha,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  Add,
  Close,
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generatePurchasePdf } from "../Accounts/PurchaseVoucherPrint";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

const PurchaseVouchers = () => {
  const [purchaseVouchers, setPurchaseVouchers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [PurchaseToApprove, setPurchaseToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);
  const baseURL = config.staticBaseURL;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedPurchase, setExpandedPurchase] = useState(null);
  const [isExcelLoading, setExportLoading] = useState(false);

  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const dateInputRef = useRef(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [filters, setFilters] = useState({
    date_range: "today",
    po_no: "",
    supplier: "",
    invoice_date: "",
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const tableHeadBg = isDark ? "#252b38" : "#f0f4ff";
  const rowBg = (index) =>
    isDark
      ? index % 2 === 0
        ? "#20242e" // even row - dark (solid)
        : "#1a1d26" // odd row - dark (solid)
      : index % 2 === 0
        ? "#fafafa" // even row - light (same as before)
        : "#ffffff"; // odd row - light (same as before)

  const rowHoverBg = isDark ? "#2c3242" : "#e3f2fd";

  // ✅✅✅ FIX #5 ke liye: mobile sticky footer ka SOLID bg
  const stickyFooterBg = isDark ? "#252b38" : "#e3f2fd";

  // ── Status border color ──
  const getStatusBorderColor = (status) => {
    if (status === "Approved") return "#4caf50";
    if (status === "Rejected") return "#f44336";
    return "#ff9800";
  };

  const handleExportToExcel = async () => {
    try {
      if (purchaseVouchers.length === 0) {
        toast.warning("No data available to export");
        return;
      }
      setExportLoading(true);
      const exportParams = { ...filters };
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        exportParams.from_date = customDateRange.from;
        exportParams.to_date = customDateRange.to;
      }
      const blob = await ExportPurchaseVouchersToExcel(exportParams);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
      link.download = `purchase_vouchers_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(isExcelLoading);
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        error.response?.data?.message || "Failed to export data to Excel",
      );
    } finally {
      setExportLoading(false);
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };
    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVouchers(1, true, filters);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters, customDateRange]);

  const fetchVouchers = async (
    pageNum = 1,
    reset = false,
    filterValues = filters,
  ) => {
    const params = { ...filterValues };
    if (
      filterValues.date_range === "custom" &&
      customDateRange.from &&
      customDateRange.to
    ) {
      params.from_date = customDateRange.from;
      params.to_date = customDateRange.to;
    }
    const data = await GetPurchaseVoucherList(params, pageNum);
    if (reset || pageNum === 1) {
      setPurchaseVouchers(
        Array.isArray(data) ? data.results : data.results || [],
      );
    } else {
      setPurchaseVouchers((prev) => [
        ...prev,
        ...(Array.isArray(data) ? data.results : data.results || []),
      ]);
    }
    setHasMore(Array.isArray(data) ? data.next !== null : data.next !== null);
    setPage(pageNum);
  };

  const fetchMoreData = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVouchers(nextPage, false, filters);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({
      date_range: "today",
      po_no: "",
      supplier: "",
      invoice_date: "",
    });
    setCustomDateRange({ from: "", to: "" });
    setSelectingDate(null);
    setPage(1);
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleDelete = async (voucherId) => {
    try {
      await DeletePurchaseVoucher(voucherId);
      toast.success("Deleted successfully!");
      fetchVouchers(page, true, filters);
    } catch (error) {
      console.error("Delete error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Delete failed.");
    }
  };

  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }
    try {
      const updated = await ApprovePurchaseVoucher(
        voucher.purchase_voucher_no,
        approvedById,
        voucher.status,
        remarkInput,
      );
      setPurchaseVouchers((prev) =>
        prev.map((item) =>
          item.purchase_voucher_no === updated.purchase_voucher_no
            ? updated
            : item,
        ),
      );
      if (voucher.status === "Approved") {
        toast.success("Purchase Voucher approved successfully!");
      } else if (voucher.status === "Rejected") {
        toast.error("Purchase Voucher rejected!");
      }
    } catch (error) {
      console.error("Approval Error:", error);
      if (voucher.status === "Approved") {
        toast.error("Failed to approve Purchase Voucher.");
      } else if (voucher.status === "Rejected") {
        toast.error("Failed to reject Purchase Voucher.");
      }
    }
  };

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <ToastContainer theme={isDark ? "dark" : "light"} />

      {/* ── Filter Section ── */}
      <Box
        sx={{
          mt: { xs: 3, sm: 1, md: 1 },
          mb: 2,
          width: "100%",
          flexShrink: 0,
          pt: 1,
          pb: 1,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Date Range */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.6rem" },
              }}
            />
          </Grid>

          {/* PO No */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Purchase Order No"
              name="po_no"
              size="small"
              value={filters.po_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* Supplier */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Supplier"
              name="supplier"
              size="small"
              value={filters.supplier}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* Invoice Date */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Invoice Date"
              name="invoice_date"
              type="date"
              size="small"
              value={filters.invoice_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
            />
          </Grid>

          {/* Reset */}
          <Grid item xs={6} sm={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export */}
          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportToExcel}
              disabled={isExcelLoading || purchaseVouchers.length === 0}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isExcelLoading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── Date Range Menu ── */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
            "& .MuiTypography-root": { fontSize: "0.7rem" },
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
            sx={{
              "&:hover": {
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.primary.main, 0.1)
                    : alpha(t.palette.primary.main, 0.06),
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {range.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.65rem", color: "text.secondary" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs
              ? (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.primary.main, 0.15)
                    : "#e3f2fd"
              : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: showCustomDateInputs ? 600 : 400,
              color: "text.primary",
            }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? alpha(t.palette.common.white, 0.04)
                  : "#f5f5f5",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              sx={{ mb: 1, display: "block", color: "text.secondary" }}
            >
              Select Date Range
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max:
                    tempCustomDateRange.to ||
                    new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": { height: 40, fontSize: "0.75rem" },
                  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                }}
              />
            </Box>

            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.success.main, 0.15)
                      : "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "success.main",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>
      <Box
        id="purchase-scroll-container"
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {/* ── InfiniteScroll ── */}
        <InfiniteScroll
          dataLength={purchaseVouchers.length}
          next={fetchMoreData}
          hasMore={hasMore}
          scrollableTarget="purchase-scroll-container"
          loader={
            <Typography
              sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
            >
              Loading...
            </Typography>
          }
        >
          {isMobile ? (
            /* ── MOBILE CARDS ── */
            <Box sx={{ bgcolor: "background.paper" }}>
              {purchaseVouchers.map((voucher, index) => (
                <Box key={voucher.purchase_voucher_no || index}>
                  <Box
                    onClick={() =>
                      setExpandedPurchase(
                        expandedPurchase === voucher.purchase_voucher_no
                          ? null
                          : voucher.purchase_voucher_no,
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      cursor: "pointer",
                      bgcolor: rowBg(index),
                      borderLeft: `4px solid ${getStatusBorderColor(
                        voucher.status,
                      )}`,
                      transition: "background-color 0.15s ease",
                      "&:active": { bgcolor: "action.selected" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "text.disabled",
                            minWidth: 25,
                          }}
                        >
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.85rem",
                            flex: 1,
                            color: "text.primary",
                          }}
                        >
                          {voucher.purchase_number}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          ₹
                          {Number(voucher.total_amount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                        <Chip
                          label={
                            voucher.payment_status === "Paid"
                              ? "Paid"
                              : voucher.payment_status === "Unpaid"
                                ? "Unpaid"
                                : "Partial"
                          }
                          color={
                            voucher.payment_status === "Paid"
                              ? "success"
                              : voucher.payment_status === "Unpaid"
                                ? "error"
                                : "warning"
                          }
                          size="small"
                          sx={{
                            height: "10px",
                            transform: "scale(0.8)",
                            "& .MuiChip-label": { padding: "0 6px" },
                          }}
                        />
                      </Box>
                    </Box>

                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedPurchase === voucher.purchase_voucher_no ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse
                    in={expandedPurchase === voucher.purchase_voucher_no}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.common.white, 0.05)
                            : alpha(t.palette.primary.main, 0.04),
                        borderLeft: `4px solid ${getStatusBorderColor(
                          voucher.status,
                        )}`,
                      }}
                    >
                      {/* Amount Summary Grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 0.8,
                          mb: 1,
                          p: 1,
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.12)
                              : "#e3f2fd",
                          borderRadius: "4px",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "primary.main",
                              letterSpacing: "0.5px",
                              fontWeight: 600,
                            }}
                          >
                            TOTAL
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "primary.main",
                            }}
                          >
                            ₹
                            {Number(voucher.total_amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "success.main",
                              letterSpacing: "0.5px",
                              fontWeight: 600,
                            }}
                          >
                            PAID
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "success.main",
                            }}
                          >
                            ₹
                            {Number(voucher.receipt_amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "error.main",
                              letterSpacing: "0.5px",
                              fontWeight: 600,
                            }}
                          >
                            BALANCE
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "error.main",
                            }}
                          >
                            ₹
                            {Number(voucher.balance_amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Details Grid */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                            }}
                          >
                            PROJECT
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {voucher.project_name || "N/A"}
                          </Typography>
                        </Box>

                        {[
                          {
                            label: "DATE",
                            value: formatDate(voucher.created_at),
                          },
                          {
                            label: "PURCHASE ORDER",
                            value: voucher.purchase_order || "N/A",
                          },
                        ].map(({ label, value }) => (
                          <Box key={label}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 600,
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        ))}

                        {voucher.transporter && (
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 600,
                              }}
                            >
                              TRANSPORTER
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {voucher.transporter}
                            </Typography>
                          </Box>
                        )}

                        {voucher.lr_no && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 600,
                              }}
                            >
                              LR NO
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {voucher.lr_no}
                            </Typography>
                          </Box>
                        )}

                        {voucher.vehicle_no && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 600,
                              }}
                            >
                              VEHICLE NO
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {voucher.vehicle_no}
                            </Typography>
                          </Box>
                        )}

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                            }}
                          >
                            CREATED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "text.secondary",
                            }}
                          >
                            {voucher.created_by_name || "N/A"}
                          </Typography>
                        </Box>

                        {voucher.approved_name && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "text.disabled",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                fontWeight: 600,
                              }}
                            >
                              APPROVED BY
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {voucher.approved_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {voucher.remarks && (
                        <Box sx={{ mb: 0.8 }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "text.disabled",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 600,
                              mb: 0.3,
                            }}
                          >
                            REMARKS
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              fontStyle: "italic",
                              lineHeight: 1.3,
                            }}
                          >
                            {voucher.remarks}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1, borderColor: "divider" }} />

                      {/* Mobile Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {userRole != "Staff" && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApproveAnchorEl(true);
                              setPurchaseToApprove(voucher);
                            }}
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.primary.main, 0.15)
                                  : "#e3f2fd",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              "&:hover": {
                                bgcolor: (t) =>
                                  t.palette.mode === "dark"
                                    ? alpha(t.palette.primary.main, 0.28)
                                    : "#bbdefb",
                              },
                            }}
                          >
                            <Visibility
                              sx={{ fontSize: "1rem", color: "primary.main" }}
                            />
                          </IconButton>
                        )}

                        <Box sx={{ display: "flex", gap: 0.6 }}>
                          {/* Edit */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const route = voucher.purchase_order
                                ? "/dashboard/pe-with-po"
                                : "/dashboard/pe-without-po";
                              navigate(route, {
                                state: {
                                  issue: {
                                    ...voucher,
                                    id: voucher.purchase_voucher_no,
                                  },
                                },
                              });
                            }}
                            disabled={voucher.status === "Approved"}
                            sx={{
                              bgcolor: (t) =>
                                voucher.status === "Approved"
                                  ? "action.disabledBackground"
                                  : t.palette.mode === "dark"
                                    ? alpha(t.palette.primary.main, 0.15)
                                    : "#e3f2fd",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: (t) =>
                                  t.palette.mode === "dark"
                                    ? alpha(t.palette.primary.main, 0.28)
                                    : "#bbdefb",
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            <Edit
                              sx={{
                                fontSize: "1rem",
                                color:
                                  voucher.status === "Approved"
                                    ? "action.disabled"
                                    : "primary.main",
                                transform: "scale(0.6)",
                              }}
                            />
                          </IconButton>

                          {/* Delete */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setVoucherToDelete(voucher.purchase_voucher_no);
                            }}
                            disabled={voucher.status === "Approved"}
                            sx={{
                              bgcolor: (t) =>
                                voucher.status === "Approved"
                                  ? "action.disabledBackground"
                                  : t.palette.mode === "dark"
                                    ? alpha(t.palette.error.main, 0.15)
                                    : "#ffebee",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: (t) =>
                                  t.palette.mode === "dark"
                                    ? alpha(t.palette.error.main, 0.28)
                                    : "#ffcdd2",
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            <Delete
                              sx={{
                                fontSize: "1rem",
                                color:
                                  voucher.status === "Approved"
                                    ? "action.disabled"
                                    : "error.main",
                                transform: "scale(0.6)",
                              }}
                            />
                          </IconButton>

                          {/* Print */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              generatePurchasePdf(voucher);
                            }}
                            sx={{
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha("#f57c00", 0.15)
                                  : "#fff3e0",
                              borderRadius: "6px",
                              width: 32,
                              height: 32,
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: (t) =>
                                  t.palette.mode === "dark"
                                    ? alpha("#f57c00", 0.28)
                                    : "#ffe0b2",
                                transform: "scale(1.05)",
                              },
                            }}
                          >
                            <Print
                              sx={{
                                fontSize: "1rem",
                                color: "#f57c00",
                                transform: "scale(0.6)",
                              }}
                            />
                          </IconButton>

                          {/* Attachment */}
                          {typeof voucher.attachment === "string" ? (
                            <a
                              href={`${voucher.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: (t) =>
                                    t.palette.mode === "dark"
                                      ? alpha(t.palette.success.main, 0.15)
                                      : "#e8f5e9",
                                  borderRadius: "6px",
                                  width: 32,
                                  height: 32,
                                  "&:hover": {
                                    bgcolor: (t) =>
                                      t.palette.mode === "dark"
                                        ? alpha(t.palette.success.main, 0.28)
                                        : "#c8e6c9",
                                    transform: "scale(1.05)",
                                  },
                                  transition: "all 0.2s",
                                }}
                              >
                                <Visibility
                                  sx={{
                                    fontSize: "1rem",
                                    color: "success.main",
                                    transform: "scale(0.6)",
                                  }}
                                />
                              </IconButton>
                            </a>
                          ) : null}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>

                  <Divider />
                </Box>
              ))}

              {/* ✅✅✅ FIX #5: Mobile Total Summary — ye bhi STICKY (bottom: 0) hai,
                isliye iska bg bhi SOLID hona chahiye. ❌ pehle alpha(0.12) tha,
                dark mode me scroll pe peeche ke cards dikh jate the. */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: stickyFooterBg, // ✅ SOLID
                  position: "sticky",
                  bottom: 0,
                  borderTop: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                {[
                  {
                    label: "Total Amount:",
                    color: "primary.main",
                    value: purchaseVouchers.reduce(
                      (sum, v) => sum + parseFloat(v.total_amount || 0),
                      0,
                    ),
                  },
                  {
                    label: "Total Paid:",
                    color: "success.main",
                    value: purchaseVouchers.reduce(
                      (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                      0,
                    ),
                  },
                  {
                    label: "Total Balance:",
                    color: "error.main",
                    value: purchaseVouchers.reduce(
                      (sum, v) => sum + parseFloat(v.balance_amount || 0),
                      0,
                    ),
                  },
                ].map(({ label, color, value }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: "bold", color }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: "bold", color }}
                    >
                      ₹
                      {value.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1600,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                {/* Header sticky cells (View / S.No. / Action) sab tableHeadBg
                  use karte hain jo ab SOLID hai (FIX #1) ✅ */}
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: tableHeadBg, // ✅ ab SOLID
                      fontSize: "0.7rem",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                          zIndex: 3,
                        }}
                      >
                        View
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 50 : 0,
                        backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                        zIndex: 3,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell>Voucher No</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Invoice No.</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Total Amt</TableCell>
                    <TableCell>MOS</TableCell>
                    <TableCell>Purchase Order</TableCell>
                    <TableCell>LR No</TableCell>
                    <TableCell>Vehicle No</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: `${tableHeadBg} !important`, // ✅ ab SOLID
                        zIndex: 3,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {purchaseVouchers.map((voucher, index) => (
                    /* ✅✅✅ FIX #4: "hover" prop HATA DIYA + hover color SOLID (rowHoverBg) */
                    <TableRow
                      key={voucher.purchase_voucher_no || index}
                      sx={{
                        backgroundColor: rowBg(index),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: `${rowHoverBg} !important`, // ❌ pehle dark me alpha(0.1) tha
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "inherit", // ✅ row ab SOLID hai — inherit theek hai
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              setApproveAnchorEl(true);
                              setPurchaseToApprove(voucher);
                            }}
                            sx={{ padding: 0 }}
                          >
                            <Visibility
                              sx={{ fontSize: "1rem", color: "primary.main" }}
                            />
                          </IconButton>
                        </TableCell>
                      )}

                      <TableCell
                        sx={{
                          position: "sticky",
                          left: userRole != "Staff" ? 50 : 0,
                          bgcolor: "inherit", // ✅ theek hai ab
                          zIndex: 2,
                        }}
                      >
                        {index + 1}
                      </TableCell>

                      <TableCell>{voucher.purchase_number}</TableCell>
                      <TableCell>{voucher.supplier_name}</TableCell>
                      <TableCell>{voucher.invoice_number}</TableCell>
                      <TableCell>{voucher.invoice_date}</TableCell>
                      <TableCell>
                        {Number(voucher.final_amount || 0).toLocaleString(
                          "en-IN",
                        )}
                      </TableCell>
                      <TableCell>
                        {voucher.project_name?.length > 30 ? (
                          <Tooltip title={voucher.project_name} arrow>
                            <span>
                              {voucher.project_name.substring(0, 30)}...
                            </span>
                          </Tooltip>
                        ) : (
                          voucher.project_name
                        )}
                      </TableCell>
                      <TableCell>{voucher.purchase_order}</TableCell>
                      <TableCell>{voucher.lr_no}</TableCell>
                      <TableCell>{voucher.vehicle_no}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            voucher.payment_status === "Paid"
                              ? "Paid"
                              : voucher.payment_status === "Unpaid"
                                ? "Unpaid"
                                : "Partial"
                          }
                          color={
                            voucher.payment_status === "Paid"
                              ? "success"
                              : voucher.payment_status === "Unpaid"
                                ? "error"
                                : "warning"
                          }
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(voucher.created_at)}</TableCell>
                      <TableCell>{voucher.created_by_name}</TableCell>

                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "inherit", // ✅ theek hai ab
                          zIndex: 2,
                          minWidth: 180,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            sx={{
                              padding: 0,
                              paddingLeft: 1,
                              paddingRight: 1,
                            }}
                            size="small"
                            color="primary"
                            onClick={() => {
                              const route = voucher.purchase_order
                                ? "/dashboard/pe-with-po"
                                : "/dashboard/pe-without-po";
                              navigate(route, {
                                state: {
                                  issue: {
                                    ...voucher,
                                    id: voucher.purchase_voucher_no,
                                  },
                                },
                              });
                            }}
                          >
                            <Edit transform="scale(0.6)" />
                          </IconButton>

                          <IconButton
                            sx={{
                              padding: 0,
                              paddingLeft: 1,
                              paddingRight: 1,
                            }}
                            size="small"
                            color="error"
                            onClick={(event) => {
                              setAnchorEl(event.currentTarget);
                              setVoucherToDelete(voucher.purchase_voucher_no);
                            }}
                          >
                            <Delete transform="scale(0.6)" />
                          </IconButton>

                          <IconButton
                            sx={{
                              padding: 0,
                              paddingLeft: 1,
                              paddingRight: 1,
                            }}
                            color="warning"
                            size="small"
                            onClick={() => generatePurchasePdf(voucher)}
                          >
                            <Print transform="scale(0.6)" />
                          </IconButton>

                          <span
                            style={{
                              display: "inline-block",
                              width: 36,
                              textAlign: "center",
                            }}
                          >
                            {voucher?.attachment ? (
                              <a
                                href={`${voucher.attachment}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <IconButton
                                  sx={{
                                    padding: 0,
                                    paddingLeft: 1,
                                    paddingRight: 1,
                                  }}
                                  color="info"
                                  size="small"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </a>
                            ) : (
                              <span
                                style={{
                                  display: "inline-block",
                                  width: 24,
                                }}
                              />
                            )}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Totals Row (desktop) — ye sticky NAHI hai, isliye pehle jaisa
                    hi rehne diya hai (ispe see-through problem nahi aati) */}
                  <TableRow
                    sx={{
                      "& td": {
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? `${alpha(
                                t.palette.primary.main,
                                0.18,
                              )} !important`
                            : "#E3F2FD !important",
                        color: "text.primary !important",
                        fontWeight: "bold !important",
                      },
                    }}
                  >
                    <TableCell colSpan={6} align="right">
                      Totals:
                    </TableCell>
                    <TableCell>
                      {purchaseVouchers
                        .reduce(
                          (sum, v) => sum + parseFloat(v.final_amount || 0),
                          0,
                        )
                        .toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </TableCell>
                    <TableCell colSpan={13}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </Box>
      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setVoucherToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
            Are you sure?
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                if (voucherToDelete) {
                  await handleDelete(voucherToDelete);
                }
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Approve Preview Popover ── */}
      <Popover
        open={Boolean(approveAnchorEl)}
        onClose={() => {
          setApproveAnchorEl(null);
          setPurchaseToApprove(null);
          setRemarkInput("");
          setShowRemarkForIndentId(null);
          setActionType(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        PaperProps={{
          sx: {
            width: 1300,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.2)",
          },
        }}
      >
        {PurchaseToApprove && (
          <>
            {/* Fixed Header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.primary.main, 0.12)
                    : "#f4f6f8",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: "text.primary" }}
              >
                Purchase Voucher Preview
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Voucher No: {PurchaseToApprove.purchase_number}
              </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flex: 1,
                bgcolor: "background.paper",
              }}
            >
              {/* Info Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>VOUCHER NO.:</strong>{" "}
                    {PurchaseToApprove.purchase_number}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>MOS:</strong> {PurchaseToApprove.project_name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.5,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>SUPPLIER:</strong> {PurchaseToApprove.supplier_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>LR NO.:</strong> {PurchaseToApprove.lr_no}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>VEHICLE NO.:</strong> {PurchaseToApprove.vehicle_no}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>INVOICE NO.:</strong>{" "}
                    {PurchaseToApprove.invoice_number}
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>PAYMENT STATUS:</strong>{" "}
                    {PurchaseToApprove.payment_status}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, color: "text.primary" }}
                  >
                    <strong>CREATED BY:</strong>{" "}
                    {PurchaseToApprove.created_by_name}
                  </Typography>
                </Box>
              </Box>

              {/* Items Table — yaha tableHeadBg/rowBg ab SOLID hain ✅ */}
              <TableContainer
                component={Box}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      color: "text.primary",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: tableHeadBg,
                        "& th": {
                          fontWeight: 700,
                          py: 1,
                          whiteSpace: "nowrap",
                          color: "text.primary",
                          backgroundColor: `${tableHeadBg} !important`,
                        },
                      }}
                    >
                      <TableCell>Sl.</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell>PO Qty</TableCell>
                      <TableCell>Rec Qty</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>GST %</TableCell>
                      <TableCell>GST Amt</TableCell>
                      <TableCell>Total Amt</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {PurchaseToApprove.items?.map((item, index) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          bgcolor: rowBg(index),
                          "&:nth-of-type(even)": {
                            bgcolor: rowBg(1),
                          },
                        }}
                      >
                        <TableCell sx={{ py: 0.75 }}>{index + 1}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.category_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.item_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.size_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.unit_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>{item.quantity}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.received_qty}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>₹{item.rate}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>₹{item.amount}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.gst_percentage}%
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          ₹{item.gst_amount}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          ₹{item.item_total}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Totals Row */}
                    <TableRow
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.success.main, 0.12)
                            : "#e8f5e9",
                        borderTop: "2px solid",
                        borderColor: "divider",
                      }}
                    >
                      <TableCell
                        colSpan={8}
                        align="right"
                        sx={{ fontWeight: 700, py: 1 }}
                      >
                        TOTAL:
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.total_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}></TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.gst_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.final_amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Fixed Footer */}
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.03)
                    : "#fff",
                flexShrink: 0,
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setApproveAnchorEl(null);
                  setPurchaseToApprove(null);
                  setRemarkInput("");
                  setShowRemarkForIndentId(null);
                  setActionType(null);
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default PurchaseVouchers;
